import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import clsx from 'clsx';
import { AppScreenshot } from '../../types';
import { useI18n } from '../../context/I18nContext';

export interface ImageLightboxProps {
  images: AppScreenshot[];
  /** Index of the image to show; `null` keeps the lightbox closed. */
  index: number | null;
  onChange: (index: number) => void;
  onClose: () => void;
}

const SWIPE_THRESHOLD_PX = 50;

const controlButtonClass =
  'inline-flex items-center justify-center w-11 h-11 rounded-full bg-slate-900/70 text-white ring-1 ring-white/15 backdrop-blur-sm hover:bg-slate-900/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white';

export const ImageLightbox: React.FC<ImageLightboxProps> = ({ images, index, onChange, onClose }) => {
  const { resolveText, t } = useI18n();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const touchStartX = useRef<number | null>(null);

  const isOpen = index !== null && images.length > 0;
  const total = images.length;
  const hasMultiple = total > 1;

  const goPrev = useCallback((): void => {
    if (index === null) return;
    onChange((index - 1 + total) % total);
  }, [index, total, onChange]);

  const goNext = useCallback((): void => {
    if (index === null) return;
    onChange((index + 1) % total);
  }, [index, total, onChange]);

  // Keyboard: Esc closes, arrows navigate, Tab stays inside the dialog.
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'ArrowLeft' && hasMultiple) {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight' && hasMultiple) {
        e.preventDefault();
        goNext();
      } else if (e.key === 'Tab' && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll<HTMLButtonElement>('button:not([disabled])');
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, hasMultiple, goPrev, goNext, onClose]);

  // Lock page scroll, move focus into the dialog, and restore it on close.
  useEffect(() => {
    if (!isOpen) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus();
    };
  }, [isOpen]);

  if (!isOpen || index === null) return null;

  const current = images[index];
  const caption = current.caption ? resolveText(current.caption) : undefined;

  const handleTouchStart = (e: React.TouchEvent): void => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent): void => {
    if (touchStartX.current === null || !hasMultiple) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (deltaX > SWIPE_THRESHOLD_PX) goPrev();
    else if (deltaX < -SWIPE_THRESHOLD_PX) goNext();
  };

  // Close only when the dark backdrop itself (not the image or controls) is clicked.
  const handleBackdropClick = (e: React.MouseEvent): void => {
    if (e.target === e.currentTarget) onClose();
  };

  return createPortal(
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={t.appDetail.lightboxLabel}
      className="fixed inset-0 z-[60] flex flex-col bg-slate-950/90"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Top bar: counter + close */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-3">
        <p className="text-sm font-medium text-white/80 tabular-nums" aria-live="polite">
          {hasMultiple ? `${index + 1} / ${total}` : ''}
        </p>
        <button
          ref={closeButtonRef}
          type="button"
          onClick={onClose}
          className={controlButtonClass}
          aria-label={t.appDetail.lightboxClose}
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Image stage */}
      <div
        className="relative flex-1 min-h-0 flex items-center justify-center px-4 sm:px-20"
        onClick={handleBackdropClick}
      >
        <img
          key={current.id}
          src={current.url}
          alt={resolveText(current.alt)}
          className="max-h-full max-w-full w-auto h-auto object-contain rounded-lg select-none"
          draggable={false}
        />

        {hasMultiple && (
          <>
            <button
              type="button"
              onClick={goPrev}
              className={clsx(controlButtonClass, 'absolute left-2 sm:left-6 top-1/2 -translate-y-1/2')}
              aria-label={t.appDetail.lightboxPrev}
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              type="button"
              onClick={goNext}
              className={clsx(controlButtonClass, 'absolute right-2 sm:right-6 top-1/2 -translate-y-1/2')}
              aria-label={t.appDetail.lightboxNext}
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}
      </div>

      {/* Caption + position indicator */}
      <div className="px-4 sm:px-6 pt-3 pb-5 text-center" onClick={handleBackdropClick}>
        {caption && <p className="text-sm sm:text-base font-medium text-white">{caption}</p>}
        {hasMultiple && (
          <div className="mt-3 flex items-center justify-center gap-1.5" aria-hidden="true">
            {images.map((img, i) => (
              <span
                key={img.id}
                className={clsx(
                  'h-1.5 rounded-full transition-all',
                  i === index ? 'w-5 bg-white' : 'w-1.5 bg-white/40'
                )}
              />
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};
